'use client';

import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/redux/Store';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'react-toastify';
import { FaSync, FaExclamationTriangle, FaCheckCircle, FaPlus } from 'react-icons/fa';
import './SpotlightTransactions.scss';

interface SpotlightComparison {
  txHash: string;
  timestamp: number;
  sender: string;
  function: string;
  amount: string;
  status: string;
  inDatabase: boolean;
  dbRecord?: {
    type: string;
    cost: string;
    status: string;
    createdAt: string;
  };
  issue?: string;
}

interface SpotlightTransactionsData {
  totalContractTransactions: number;
  totalDBRecords: number;
  missingInDB: number;
  comparisons: SpotlightComparison[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

const SpotlightTransactions: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const [data, setData] = useState<SpotlightTransactionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: SpotlightTransactionsData }>(
        '/admin/spotlight/transactions'
      );

      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching spotlight transactions:', error);
      toast.error('Failed to load spotlight transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordMissing = async (txHash: string) => {
    setRecording(txHash);

    try {
      const response = await api.post('/admin/spotlight/transactions/record', { txHash });

      if (response.data.success) {
        toast.success('Missing transaction recorded successfully');
        fetchData(); // Reload data
      }
    } catch (error: any) {
      console.error('Error recording transaction:', error);
      // Backend returns both `error` and `message`; check both so a
      // controller that only sets one still surfaces a useful toast.
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to record transaction';
      toast.error(msg);
    } finally {
      setRecording(null);
    }
  };

  const formatAmount = (amount: string) => {
    // Phase B: on-chain spotlight payment amounts are KLV smallest units
    // (6 decimals). Was dividing by 1e8 + labeling KLYX — both wrong.
    const amountNum = parseFloat(amount) / 1_000_000;
    return `${amountNum.toFixed(2)} KLV`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const shortenHash = (hash: string) => {
    return `${hash.substring(0, 8)}...${hash.substring(hash.length - 8)}`;
  };

  if (loading) {
    return (
      <div className="spotlight-transactions-page">
        <div className="loading">Loading spotlight transactions...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="spotlight-transactions-page">
        <div className="error">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="spotlight-transactions-page">
      <div className="page-header">
        <h1>Spotlight Transactions Tracking</h1>
        <p>Cross-reference blockchain contract transactions with database records</p>
        <button className="btn-refresh" onClick={fetchData} disabled={loading}>
          <FaSync className={loading ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon blockchain">
            <FaCheckCircle />
          </div>
          <div className="card-content">
            <h3>Contract Transactions</h3>
            <div className="card-value">{data.totalContractTransactions}</div>
            <p>Total spotlight transactions on blockchain</p>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon database">
            <FaCheckCircle />
          </div>
          <div className="card-content">
            <h3>Database Records</h3>
            <div className="card-value">{data.totalDBRecords}</div>
            <p>Recorded spotlight purchases in database</p>
          </div>
        </div>

        <div className={`summary-card ${data.missingInDB > 0 ? 'warning' : ''}`}>
          <div className="card-icon warning">
            <FaExclamationTriangle />
          </div>
          <div className="card-content">
            <h3>Missing in Database</h3>
            <div className="card-value">{data.missingInDB}</div>
            <p>Funds taken but not recorded</p>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-section">
        <h2>Transaction Comparison</h2>
        <div className="table-container">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>TX Hash</th>
                <th>Timestamp</th>
                <th>Function</th>
                <th>Amount</th>
                <th>Status</th>
                <th>In DB</th>
                <th>Issue</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.comparisons.map((tx) => (
                <tr key={tx.txHash} className={tx.issue ? 'has-issue' : ''}>
                  <td>
                    <a
                      href={`https://testnet.kleverscan.org/transaction/${tx.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tx-hash"
                    >
                      {shortenHash(tx.txHash)}
                    </a>
                  </td>
                  <td>{formatDate(tx.timestamp)}</td>
                  <td>
                    <span className={`function-badge ${tx.function}`}>
                      {tx.function.replace('purchase', '').replace('Spotlight', '')}
                    </span>
                  </td>
                  <td>{formatAmount(tx.amount)}</td>
                  <td>
                    <span className={`status-badge ${tx.status}`}>{tx.status}</span>
                  </td>
                  <td>
                    {tx.inDatabase ? (
                      <span className="badge success">
                        <FaCheckCircle /> Yes
                      </span>
                    ) : (
                      <span className="badge error">
                        <FaExclamationTriangle /> No
                      </span>
                    )}
                  </td>
                  <td>
                    {tx.issue ? (
                      <span className="issue-text">{tx.issue}</span>
                    ) : (
                      <span className="no-issue">-</span>
                    )}
                  </td>
                  <td>
                    {!tx.inDatabase && (
                      <button
                        className="btn-record"
                        onClick={() => handleRecordMissing(tx.txHash)}
                        disabled={recording === tx.txHash}
                      >
                        <FaPlus /> {recording === tx.txHash ? 'Recording...' : 'Record'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SpotlightTransactions;
